import { OpenAIOutlined, SyncOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import NoImage from 'assets/no-image.png';
import { AxiosResponse } from 'axios';
import Drawer from 'components/Drawer';
import TaskInfo from 'components/TaskInfo';
import { useTranslation } from 'react-i18next';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { DataPagination, DataVerificationLog, Stakeholder } from 'types/common';
import {
  getStakeholderTypeForCountry,
  renderDate,
  renderInfoValue,
} from 'utils/helpers';
import { stakeholderRoleToType } from '../helper';
import useMutateVerifyOpenAI from '../hooks/useMutateVerifyOpenAI';

type VerificationDrawerProps = {
  country: string;
  dataId?: string;
  open: boolean;
  onClose: () => void;
  onDataVerify?: (data: Stakeholder) => void;
  params?: { [key: string]: string | string[] | number | boolean | undefined };
};

const VerificationDrawer = ({
  country,
  dataId,
  open,
  onClose,
  params,
}: VerificationDrawerProps) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutateVerifyOpenAI();

  const data = queryClient
    .getQueryData<
      AxiosResponse<DataPagination<Stakeholder>>
    >(['stakeholder-data', params])
    ?.data?.content.find((item) => item.id === dataId);

  const verificationLogs = data?.dataVerificationLog || [];

  const { t } = useTranslation();

  const handleVerify = async () => {
    if (!dataId || !data?.role) return;

    await mutateAsync({
      stakeholderId: data?.id,
      stakeholderType: stakeholderRoleToType(data.role),
    });
  };

  const getStakeholderRole = () => {
    if (!data?.role) return '';

    const role = data.role === 'Paddy Buyer' ? 'PB' : data.role;

    return role === 'PB' ? getStakeholderTypeForCountry(country, role) : role;
  };

  const renderVerifiedBy = (log: DataVerificationLog) => {
    if (log.verifiedByAI && !log.verificationTrigger) {
      return 'N/A';
    }
    if (log.verificationMethod === 'SYSTEM') {
      return 'System';
    }
    if (
      log.verificationTrigger !== 'USER_INITIATED' &&
      log.verificationMethod === 'OPEN_AI'
    ) {
      return 'AI Agent (via Submission)';
    }
    return log.createdBy;
  };

  return (
    <Drawer
      title="Stakeholder Review Details"
      open={open}
      onClose={onClose}
      loading={isPending}
      content={
        <Flex gap={16} vertical>
          <Card size="small">
            <Flex justify="space-between" align="center">
              <Flex gap={12} align="center">
                {data?.documentList?.[0]?.url ? (
                  <PhotoProvider>
                    <PhotoView src={data.documentList[0].url}>
                      <img
                        src={data.documentList[0].url}
                        alt={t('stakeholders.paddyBuyerProfile')}
                        className="img-fluid me-2 farmer-profile"
                      />
                    </PhotoView>
                  </PhotoProvider>
                ) : (
                  <Avatar src={NoImage} size={44} />
                )}
                <div>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {data?.name}
                  </Typography.Title>
                  <Typography className="info-label">
                    {getStakeholderRole()}
                  </Typography>
                </div>
              </Flex>

              <Button
                type="primary"
                onClick={handleVerify}
                loading={isPending}
                icon={<SyncOutlined />}
                style={{ float: 'right' }}
              >
                Verify
              </Button>
            </Flex>
          </Card>

          <span className="text-strong text-large">Review Log</span>

          {verificationLogs?.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_DEFAULT}
              description="No logs available"
            />
          )}
          {verificationLogs?.map((log, index) => (
            <Card key={'log-' + index} style={{ position: 'relative' }}>
              {(log?.verifiedByAI || log.verificationMethod === 'OPEN_AI') && (
                <Tooltip title="Verified by AI Agent">
                  <span
                    style={{
                      display: 'flex',
                      float: 'right',
                      fontSize: '20px',
                      position: 'absolute',
                      right: '16px',
                      zIndex: 1,
                    }}
                  >
                    <OpenAIOutlined />
                  </span>
                </Tooltip>
              )}
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <span
                    className="text-strong"
                    style={{
                      color: log?.dataVerifiedStatus ? '#389E0D' : '#FF4D4F',
                    }}
                  >
                    {log.dataVerifiedStatus ? 'Verified' : 'Unverified'}
                  </span>
                </Col>
                <Col span={12}>
                  <TaskInfo
                    header="Reviewed At"
                    value={renderDate(
                      log.reportedDate,
                      '-',
                      'DD MMM YYYY HH:mm:ss',
                    )}
                  />
                </Col>
                <Col span={12}>
                  <TaskInfo
                    header="Reviewed By"
                    value={log?.createdBy ? renderVerifiedBy(log) : '-'}
                  />
                </Col>
                <Col span={24}>
                  <TaskInfo
                    header="Reason"
                    value={renderInfoValue(log.reason || '-')}
                  />
                </Col>
              </Row>
            </Card>
          ))}
        </Flex>
      }
    />
  );
};

export default VerificationDrawer;
